<template>
    <div class="q-pa-md">
        <q-toolbar class="text-white shadow-2" style="background:rgb(39 170 225);">
            <q-toolbar-title>Claim your domain in seconds</q-toolbar-title>
        </q-toolbar>
        <q-form ref="form" @submit="submit">
            <div class="full-width content-center q-pt-md">
                <q-input v-model="subdomain"
                         class="q-mr-md full-width"
                         :loading="isLoading"
                         :error="isError"
                         debounce="500"
                         :maxlength="maxSubdomainLength"
                         :rules="subdomainRules"
                         clearable
                         label-slot
                         outlined borderless counter>
                    <template v-slot:label>Your-Domain</template>
                    <template v-slot:hint>
                        <div id="subdomain-life-preview">
                            https://<span class="text-deep-orange-4">{{ subdomain }}</span>.{{ appDomain }}
                        </div>
                    </template>
                </q-input>
            </div>
            <div v-if="isLoading" class="text-center text-bold" style="color:#5187c5;">
                <q-spinner-pie color="orange" size="1.7em"/>
                checking domain ...
            </div>

            <div v-if="isError" class="text-center text-bold">
                <span class="q-ml-sm text-red">Unable to check domain availability. Please, try later</span>
            </div>

            <div v-if="isAvailable === false && isReserved === false" class="text-negative text-center text-bold q-mt-xl">
                <q-icon color="negative" name="fa-solid fa-triangle-exclamation" size="1.7em"/>
                <span class="q-ml-sm text-negative">Sorry, this Domain is already registered</span>
            </div>
            <div v-if="isAvailable === false && isReserved === true" class="text-negative text-center text-bold q-mt-xl">
                <q-icon color="negative" name="fa-solid fa-triangle-exclamation" size="1.7em"/>
                <span class="q-ml-sm text-negative">Sorry, this Domain is reserved</span>
                <p>Contact us on <a href="https://linkedin.com/company/memo-link" target="_blank">LinkedIn</a> to claim it</p>
            </div>
            <div v-if="isTooShortDomainLength" class="q-mt-lg">
                <q-icon color="warning" name="fa-solid fa-triangle-exclamation" size="1.7em"/>
                <span class="q-ml-sm text-warning">
                            In order to unlock Domains shorter than {{ minSubdomainLength }} characters for every
                            Memo.Link user<br>
                            please consider donating any token on Ethereum (L1 or L2)
                            or Solana at the following addresses:<br/>
                    <a href="https://app.ens.domains/memolink.eth"
                       target="_blank">memolink.eth</a> = <b>[0x6Eff...2F85]</b><br/>
                        <a href="https://www.sns.id/domain/memolink"
                           target="_blank">memolink.sol</a> = <b>[5qMi...oTLr]</b>
                        </span>
            </div>
            <div v-if="isAvailable === true" class="text-positive text-center text-bold q-mt-xl">
                <p>
                    <q-icon color="positive" name="fa-solid fa-circle-check" size="1.7em"/>
                    <span class="q-ml-sm">Congrats, this Domain is still available!</span>
                </p>
                <p v-if="!isTooShortDomainLength">
                    <a href="javascript:void(0);" @click.prevent="openRegisterDialog" class="text-positive">
                        Click here to register it for FREE before it gets taken!
                    </a>
                </p>
            </div>

        </q-form>
    </div>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { useQuasar } from 'quasar'
import { useSubDomainService } from '../../services/subDomainService'
import { useStore } from 'vuex'

const quasar = useQuasar()
const appDomain = inject('appDomain')
const subdomainService = useSubDomainService()

const form = ref(null)
const subdomain = ref('')
const isError = ref(false)
const isLoading = ref(false)
const isAvailable = ref(null)
const isReserved = ref(null)
const eventBus = inject('eventBus')
const store = useStore()
const limits = computed(() => store.getters.getLimits)
const minSubdomainLength = computed(() => parseInt(limits?.value?.getSubdomainMinLength()?.value))
const maxSubdomainLength = computed(() => parseInt(limits?.value?.getSubdomainMaxLength()?.value))
const isTooShortDomainLength = computed(() => subdomain?.value?.length >= 2 && subdomain?.value?.length < minSubdomainLength.value)
const subdomainRules = ref([
    v => v && v.trim().length > 0 || 'Please fill this field',
    v => v && v.trim().length >= 2 || `Minimal length is 2`,
    v => v && v.trim().length <= maxSubdomainLength.value || `Maximal length is ${maxSubdomainLength.value}`,
    v => v && /^[A-Za-z](?:[A-Za-z\d-]*[A-Za-z\d])$/.test(v) || 'Sorry, only latin characters, numbers and hyphen are allowed.'
]);

async function submit () {
    if(isTooShortDomainLength.value !== false){
        return;
    }

    isLoading.value = true

    subdomainService
        .checkIsAvailable(subdomain.value)
        .catch(() => isError.value = true)
        .then((response) => {
            isAvailable.value = response.data.data.isAvailable;
            isReserved.value = response.data.data.isReserved;
        })
        .finally(() => isLoading.value = false)
}

watch(subdomain, async (newValue) => {
    isAvailable.value = null
    isError.value = false

    if (newValue == null || newValue.length < 2) {
        return
    }

    //await form.value.validate();

    if (await form.value.validate()) {
        await submit()
    }
})

function openRegisterDialog () {
    localStorage.newDomain = subdomain.value
    eventBus.emit('showLoginDialog')
}
</script>

<style scoped>
form {
    background: #ffffff;
    border: 1px solid #a3a3a3;
    padding: 20px;
    box-sizing: border-box;
    z-index: 1;
}

.middle {
    vertical-align: middle;
    display: inline-block;
    font-size: 1rem;
}
.q-toolbar__title {
    font-size: 14px;
    text-wrap: unset;
}
#subdomain-life-preview {
    color: rgb(39, 170, 225);
    background: rgb(243 242 242);
    padding: 5px 10px;
    font-size: 1.2em;
    margin: 0 -44px 10px -12px;
    border-radius: 4px;
}

</style>

<style>
.q-field__bottom .q-field__counter{
    padding: 0;
    line-height: 2;
}
</style>
